import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
/** The shape the database CHECK enforces (`<table>_<column>_format`); the zone itself is checked here. */
export const TIME_ZONE_SHAPE = /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const fail = (field, rule) => new DomainValidationError(field, rule);
const formatters = new Map();
// A formatter per zone, made through the runtime's own zone database (no dependency). It throws a
// RangeError for a name it does not know.
function formatterFor(zone) {
    let formatter = formatters.get(zone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            calendar: "gregory",
            numberingSystem: "latn",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
        formatters.set(zone, formatter);
    }
    return formatter;
}
/**
 * Shape first, then the runtime must know the zone. The message names the field and the rule, never
 * the rejected value.
 */
export function ianaTimeZone(value, field = "timeZone") {
    if (typeof value !== "string" || !TIME_ZONE_SHAPE.test(value)) {
        throw fail(field, "must be an IANA time zone name");
    }
    try {
        formatterFor(value);
    }
    catch {
        throw fail(field, "must be a known IANA time zone");
    }
    return value;
}
/** A real calendar date as `YYYY-MM-DD`. Dates are plain text everywhere, so they never shift with a zone. */
export function localDate(value, field) {
    const match = typeof value === "string" ? DATE.exec(value) : null;
    if (match === null)
        throw fail(field, "must be a YYYY-MM-DD date");
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCFullYear() !== year ||
        probe.getUTCMonth() !== month - 1 ||
        probe.getUTCDate() !== day) {
        throw fail(field, "must be a real calendar date");
    }
    return value;
}
/** A wall time, `HH:MM` or `HH:MM:SS`, normalized to `HH:MM:SS` (the stored form). */
export function localTimeOfDay(value, field) {
    const match = typeof value === "string" ? TIME.exec(value) : null;
    if (match === null)
        throw fail(field, "must be an HH:MM time");
    return `${match[1]}:${match[2]}:${match[3] ?? "00"}`;
}
/** The calendar date of an instant in a zone. Independent of the process time zone. */
export function localDateOf(instant, zone) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw fail("instant", "must be a valid date");
    }
    const parts = formatterFor(zone).formatToParts(instant);
    const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? "";
    return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
}
const clockFormatters = new Map();
/** Whether an instant is exactly 00:00:00.000 on the wall clock of a zone. Independent of the process zone. */
export function isLocalMidnight(instant, zone) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw fail("instant", "must be a valid date");
    }
    if (instant.getUTCMilliseconds() !== 0)
        return false;
    let formatter = clockFormatters.get(zone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            numberingSystem: "latn",
            hourCycle: "h23",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        clockFormatters.set(zone, formatter);
    }
    const parts = formatter.formatToParts(instant);
    const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? "";
    return Number(part("hour")) === 0 && Number(part("minute")) === 0 && Number(part("second")) === 0;
}
/** The trip clock's "today": the local date of `now` in the zone the dates are read in. */
export function todayIn(zone, now) {
    return localDateOf(now, zone);
}
/** What to do when a wall time happens twice. A time that does not exist is always refused. */
export const wallTimeDisambiguations = Object.freeze(["reject", "earlier", "later"]);
/**
 * `itinerary.time_not_existent`: the wall time falls in a gap of the zone (the clock jumped forward). Never fixed silently: the
 * error carries the first local time after the gap as a suggestion (`YYYY-MM-DDTHH:MM`), or `null` when it could not be found.
 * `field` names the endpoint (`timing.start` or `timing.end`) when the caller that knows it attached it with `withTimeEndpoint`.
 */
export class TimeNotExistentError extends DomainRuleViolationError {
    suggestion;
    field;
    constructor(suggestion, field = null) {
        super("itinerary.time_not_existent");
        this.name = "TimeNotExistentError";
        this.suggestion = suggestion;
        this.field = field;
    }
}
/** `itinerary.time_ambiguous`: the wall time happens twice (the clock went back) and the caller did not choose `earlier` or `later`. */
export class TimeAmbiguousError extends DomainRuleViolationError {
    field;
    constructor(field = null) {
        super("itinerary.time_ambiguous");
        this.name = "TimeAmbiguousError";
        this.field = field;
    }
}
/** The same refusal with the endpoint it is about. Any other error is returned as it is. */
export function withTimeEndpoint(error, field) {
    if (error instanceof TimeNotExistentError)
        return new TimeNotExistentError(error.suggestion, field);
    if (error instanceof TimeAmbiguousError)
        return new TimeAmbiguousError(field);
    return error;
}
const wallFormatters = new Map();
function wallFormatterFor(zone) {
    let formatter = wallFormatters.get(zone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            calendar: "gregory",
            numberingSystem: "latn",
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        wallFormatters.set(zone, formatter);
    }
    return formatter;
}
function wallPartsOf(instant, zone) {
    const parts = wallFormatterFor(zone).formatToParts(instant);
    const part = (type) => Number(parts.find((candidate) => candidate.type === type)?.value ?? "NaN");
    return {
        y: part("year"),
        mo: part("month"),
        d: part("day"),
        h: part("hour"),
        mi: part("minute"),
        s: part("second"),
    };
}
const MINUTE_MS = 60_000;
const naive = (p) => Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
/** The UTC offset of a zone at an instant, in minutes, from the runtime's own zone rules. */
export function utcOffsetMinutesAt(instant, zone) {
    const whole = Math.floor(instant.getTime() / 1000) * 1000;
    return Math.round((naive(wallPartsOf(new Date(whole), zone)) - whole) / MINUTE_MS);
}
/** Every instant at which the wall clock of the zone reads this time, earliest first: none (a gap), one, or two (an overlap). */
function wallCandidates(wall, zone) {
    const probes = [-36, -12, 0, 12, 36].map((hours) => utcOffsetMinutesAt(new Date(wall + hours * 3_600_000), zone));
    const found = new Map();
    for (const offset of new Set(probes)) {
        const instant = wall - offset * MINUTE_MS;
        if (naive(wallPartsOf(new Date(instant), zone)) === wall)
            found.set(instant, offset);
    }
    return [...found.entries()]
        .sort(([a], [b]) => a - b)
        .map(([instant, offset]) => ({ instant: new Date(instant), offset }));
}
const pad2 = (value) => String(value).padStart(2, "0");
const wallText = (p) => `${String(p.y).padStart(4, "0")}-${pad2(p.mo)}-${pad2(p.d)}T${pad2(p.h)}:${pad2(p.mi)}`;
/** The first local time after the gap that holds `wall`, found by bisection between the two offsets around it. */
function suggestionAfterGap(wall, zone) {
    const offsets = [-36, 36].map((hours) => utcOffsetMinutesAt(new Date(wall + hours * 3_600_000), zone));
    const [before, after] = offsets;
    if (before === after)
        return null;
    let low = wall - Math.max(before, after) * MINUTE_MS;
    let high = wall - Math.min(before, after) * MINUTE_MS;
    const target = utcOffsetMinutesAt(new Date(high), zone);
    if (utcOffsetMinutesAt(new Date(low), zone) === target)
        return null;
    while (high - low > MINUTE_MS) {
        const middle = low + Math.floor((high - low) / (2 * MINUTE_MS)) * MINUTE_MS;
        if (middle === low)
            break;
        if (utcOffsetMinutesAt(new Date(middle), zone) === target)
            high = middle;
        else
            low = middle;
    }
    return wallText(wallPartsOf(new Date(high), zone));
}
/**
 * Converts a wall time in a zone to the UTC instant, with explicit rules for the two places where a wall clock is not one-to-one
 * (contract 3.4): a time that does not exist (the clock jumped forward) is `itinerary.time_not_existent`, carrying the first valid
 * time after the gap as a suggestion and never moved silently (stricter than Temporal's "compatible" choice); a time that happens
 * twice is `itinerary.time_ambiguous` unless `disambiguation` is `earlier` or `later`. Pure, `Intl` only: the runtime's zone data
 * decides, never the process zone and never a device zone.
 */
export function zonedWallTimeToInstant(date, time, zone, disambiguation = "reject") {
    const day = localDate(date, "date");
    const clock = localTimeOfDay(time, "time");
    const checked = ianaTimeZone(zone, "timeZone");
    if (!wallTimeDisambiguations.includes(disambiguation)) {
        throw fail("disambiguation", "must be reject, earlier or later");
    }
    const [y, mo, d] = day.split("-").map(Number);
    const [h, mi, s] = clock.split(":").map(Number);
    const wall = Date.UTC(y, mo - 1, d, h, mi, s);
    const candidates = wallCandidates(wall, checked);
    const first = candidates[0];
    if (first === undefined)
        throw new TimeNotExistentError(suggestionAfterGap(wall, checked));
    const second = candidates[1];
    if (second === undefined) {
        return Object.freeze({
            instant: first.instant,
            utcOffsetMinutes: first.offset,
            kind: "UNIQUE",
        });
    }
    if (disambiguation === "reject")
        throw new TimeAmbiguousError();
    const chosen = disambiguation === "earlier" ? first : second;
    return Object.freeze({
        instant: chosen.instant,
        utcOffsetMinutes: chosen.offset,
        kind: disambiguation === "earlier" ? "OVERLAP_EARLIER" : "OVERLAP_LATER",
    });
}
/** Elapsed time: a plain sum on the instant line. A 24 hour block on a 23 or 25 hour day is allowed and is not an error. */
export function addElapsedMinutes(instant, minutes) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw fail("instant", "must be a valid date");
    }
    if (typeof minutes !== "number" || !Number.isFinite(minutes)) {
        throw fail("minutes", "must be a finite number");
    }
    return new Date(instant.getTime() + minutes * MINUTE_MS);
}
/** The wall clock of an instant in a zone, as `YYYY-MM-DDTHH:MM` (the minute; the seconds are dropped). Independent of the process zone. */
export function localDateTimeOf(instant, zone) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw fail("instant", "must be a valid date");
    }
    return wallText(wallPartsOf(instant, zone));
}
/** The text a client sends for an entered wall time and the store keeps: `YYYY-MM-DDTHH:MM`. */
export const enteredLocalText = (date, time) => `${date}T${localTimeOfDay(time, "time").slice(0, 5)}`;
const ENTERED_LOCAL = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/;
function driftOf(facts) {
    try {
        const { enteredLocal, timeZone, instant } = facts;
        if (enteredLocal === null || timeZone === null || instant === null)
            return null;
        const match = ENTERED_LOCAL.exec(enteredLocal);
        if (match === null)
            return null;
        const [y, mo, d] = match[1].split("-").map(Number);
        const [h, mi] = match[2].split(":").map(Number);
        const candidates = wallCandidates(Date.UTC(y, mo - 1, d, h, mi, 0), timeZone);
        if (candidates.length === 0)
            return "NOW_NONEXISTENT";
        const stored = candidates.find((candidate) => candidate.instant.getTime() === instant.getTime());
        if (stored === undefined)
            return "OFFSET_CHANGED";
        if (facts.enteredUtcOffsetMinutes !== null && stored.offset !== facts.enteredUtcOffsetMinutes) {
            return "OFFSET_CHANGED";
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Does the runtime's zone data now read an entered wall time differently from the data that wrote it (a country changed its
 * daylight saving rule at short notice)? Pure and total: it never throws, and a missing or odd input is "no drift". The stored
 * instant is never changed; the view shows both times and a person corrects the item with an edit. A time that is now an overlap
 * is not drift while the stored instant is one of its two instants: it is a recorded choice, and a unique time cannot be told
 * apart from it.
 */
export function detectLocalTimeDrift(item) {
    const start = driftOf(item.start);
    const end = driftOf(item.end);
    return Object.freeze({ start, end, drifted: start !== null || end !== null });
}
